import { useEffect, useState } from "react";
import { OfficerLayout } from "@/components/OfficerLayout";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/i18n";
import { Calculator, Scale, FileSpreadsheet, Coins } from "lucide-react";

export default function Accounting() {
  const { lang } = useLang();
  const { user } = useAuth();
  const [tab, setTab] = useState("journal");
  return (
    <OfficerLayout>
      <div>
        <div className="mb-5">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722]">Accounting</div>
          <h1 className="mt-2 font-heading text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Calculator className="w-7 h-7 text-[#0A3161]" />
            {lang === "hi" ? "द्वि-प्रविष्टि लेखा" : "Double-Entry Accounting (PRIASoft)"}
          </h1>
        </div>
        <div className="flex border-b border-slate-200 mb-4 overflow-x-auto">
          {[
            { k: "journal", label: "Journal", icon: FileSpreadsheet },
            { k: "trial", label: "Trial Balance", icon: Scale },
            { k: "balance", label: "Balance Sheet", icon: Calculator },
            { k: "fund", label: "Fund Summary", icon: Coins },
          ].map((x) => {
            const I = x.icon;
            return (
              <button key={x.k} onClick={() => setTab(x.k)} data-testid={`acc-tab-${x.k}`}
                className={`px-5 py-3 text-sm font-semibold border-b-2 inline-flex items-center gap-1.5 whitespace-nowrap ${tab === x.k ? "border-[#E87722] text-[#0A3161]" : "border-transparent text-slate-500"}`}>
                <I className="w-4 h-4" /> {x.label}
              </button>
            );
          })}
        </div>
        {tab === "journal" && <Journal user={user} />}
        {tab === "trial" && <Trial user={user} />}
        {tab === "balance" && <Balance user={user} />}
        {tab === "fund" && <Fund user={user} />}
      </div>
    </OfficerLayout>
  );
}

function Journal({ user }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const params = user?.panchayat_id ? { panchayat_id: user.panchayat_id } : {};
    api.get("/accounting/journal", { params }).then((r) => setRows(r.data));
  }, [user]);
  return (
    <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
      <table className="w-full text-sm" data-testid="journal-table">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          <tr><th className="text-left px-4 py-3">JV No.</th><th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Narration</th><th className="text-left px-4 py-3">Fund</th>
              <th className="text-left px-4 py-3">Lines</th></tr>
        </thead>
        <tbody>
          {rows.map((je) => (
            <tr key={je.id} className="border-t border-slate-100 align-top">
              <td className="px-4 py-3 font-mono text-xs">{je.entry_no}</td>
              <td className="px-4 py-3 text-xs">{je.entry_date}</td>
              <td className="px-4 py-3 max-w-sm"><div className="font-medium">{je.narration}</div></td>
              <td className="px-4 py-3 text-xs capitalize">{je.fund?.replace("_", " ")}</td>
              <td className="px-4 py-3 text-xs">
                {(je.lines || []).map((l, i) => (
                  <div key={i} className="font-mono">
                    {l.account} — {l.debit > 0 ? <span className="text-emerald-700">Dr {formatINR(l.debit)}</span> : <span className="text-[#E87722]">Cr {formatINR(l.credit)}</span>}
                  </div>
                ))}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-slate-400">No journal entries</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Trial({ user }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!user?.panchayat_id) return;
    api.get("/accounting/trial-balance", { params: { panchayat_id: user.panchayat_id } }).then((r) => setData(r.data));
  }, [user]);
  if (!data) return <div className="text-slate-500 text-sm">Loading…</div>;
  return (
    <div className="rounded-md border border-slate-200 bg-white overflow-x-auto" data-testid="trial-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          <tr><th className="text-left px-4 py-3">Account</th><th className="text-left px-4 py-3">Type</th>
              <th className="text-right px-4 py-3">Debit</th><th className="text-right px-4 py-3">Credit</th>
              <th className="text-right px-4 py-3">Balance</th></tr>
        </thead>
        <tbody>
          {data.rows.map((a) => (
            <tr key={a.account} className="border-t border-slate-100">
              <td className="px-4 py-3 font-medium">{a.account}</td>
              <td className="px-4 py-3 capitalize text-xs">{a.type}</td>
              <td className="px-4 py-3 text-right font-mono">{a.debit > 0 ? formatINR(a.debit) : "—"}</td>
              <td className="px-4 py-3 text-right font-mono">{a.credit > 0 ? formatINR(a.credit) : "—"}</td>
              <td className="px-4 py-3 text-right font-mono font-semibold">{formatINR(Math.abs(a.balance))} {a.balance >= 0 ? "Dr" : "Cr"}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-slate-50 font-bold">
          <tr>
            <td colSpan={2} className="px-4 py-3">TOTAL</td>
            <td className="px-4 py-3 text-right font-mono">{formatINR(data.total_debit)}</td>
            <td className="px-4 py-3 text-right font-mono">{formatINR(data.total_credit)}</td>
            <td className="px-4 py-3 text-right"><span className={`px-2 py-1 text-[10px] uppercase rounded ${data.balanced ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{data.balanced ? "Balanced" : "Mismatch"}</span></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function Balance({ user }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!user?.panchayat_id) return;
    api.get("/accounting/balance-sheet", { params: { panchayat_id: user.panchayat_id } }).then((r) => setData(r.data));
  }, [user]);
  if (!data) return <div className="text-slate-500 text-sm">Loading…</div>;
  const Block = ({ title, items, total }) => (
    <div className="rounded-md border border-slate-200 bg-white p-5">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#E87722] font-semibold">{title}</div>
      <h3 className="font-heading text-lg font-bold mt-1">{formatINR(total)}</h3>
      <table className="w-full text-sm mt-3">
        <tbody>{items.map((i) => (
          <tr key={i.account} className="border-t border-slate-100">
            <td className="py-1.5">{i.account}</td>
            <td className="py-1.5 text-right font-mono">{formatINR(i.balance)} {i.sign}</td>
          </tr>
        ))}{items.length === 0 && <tr><td colSpan={2} className="py-2 text-slate-400 text-xs">No entries</td></tr>}</tbody>
      </table>
    </div>
  );
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="balance-sheet">
      <Block title="Assets" items={data.assets} total={data.total_assets} />
      <Block title="Liabilities" items={data.liabilities} total={data.total_liabilities} />
      <Block title="Income" items={data.income} total={data.income.reduce((s, i) => s + i.balance, 0)} />
      <Block title="Expense" items={data.expense} total={data.expense.reduce((s, i) => s + i.balance, 0)} />
      <div className="lg:col-span-2 rounded-md border-2 border-[#0A3161] bg-[#0A3161]/5 p-5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#0A3161] font-semibold">Surplus / Deficit</div>
        <h3 className="font-heading text-2xl font-bold mt-1 text-[#0A3161]">{formatINR(data.surplus)}</h3>
      </div>
    </div>
  );
}

function Fund({ user }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!user?.panchayat_id) return;
    api.get("/accounting/fund-summary", { params: { panchayat_id: user.panchayat_id } }).then((r) => setData(r.data));
  }, [user]);
  if (!data) return <div className="text-slate-500 text-sm">Loading…</div>;
  return (
    <div className="rounded-md border border-slate-200 bg-white overflow-x-auto" data-testid="fund-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          <tr><th className="text-left px-4 py-3">Fund</th>
              <th className="text-right px-4 py-3">Debit</th><th className="text-right px-4 py-3">Credit</th>
              <th className="text-right px-4 py-3">Net Balance</th></tr>
        </thead>
        <tbody>
          {data.funds.map((f) => (
            <tr key={f.fund} className="border-t border-slate-100">
              <td className="px-4 py-3 capitalize font-medium">{f.fund.replace("_", " ")}</td>
              <td className="px-4 py-3 text-right font-mono">{formatINR(f.debit)}</td>
              <td className="px-4 py-3 text-right font-mono">{formatINR(f.credit)}</td>
              <td className="px-4 py-3 text-right font-mono font-bold">{formatINR(f.balance)}</td>
            </tr>
          ))}
          {data.funds.length === 0 && <tr><td colSpan={4} className="py-10 text-center text-slate-400">No funds</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
